import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL as API } from '../../config/api';
import { Percent, IndianRupee, Save, ShieldCheck, RefreshCw } from 'lucide-react';

export default function AdminFeeSettings({ token, onToast }) {
  const [settings, setSettings] = useState({
    turf_customer_fee_type: 'percentage',
    turf_customer_fee_value: '5',
    turf_partner_fee_type: 'percentage',
    turf_partner_fee_value: '5',
    tournament_customer_fee_type: 'percentage',
    tournament_customer_fee_value: '5',
    tournament_partner_fee_type: 'percentage',
    tournament_partner_fee_value: '5',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      if (onToast) onToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (onToast) onToast(res.data?.msg || 'Platform fee settings updated!', 'success');
    } catch (err) {
      console.error('Failed to save settings:', err);
      if (onToast) onToast(err.response?.data?.msg || 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#6B7280', fontWeight: '700' }}>
        Loading platform fee settings...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', animation: 'fadeIn 0.3s ease-out' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '900', color: '#0D1F0F', margin: '0 0 6px' }}>
            ⚙️ Dynamic Platform Fee Settings
          </h2>
          <p style={{ color: '#4B5563', fontSize: '0.9rem', margin: 0 }}>
            Configure separate platform fee structures for Turf Bookings and Tournament Registrations for both Customers and Vendors.
          </p>
        </div>
        <button
          onClick={fetchSettings}
          type="button"
          style={{ background: '#F3F4F6', border: '1px solid #D1D5DB', padding: '8px 14px', borderRadius: '10px', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#374151' }}
        >
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        
        {/* TURF BOOKING SETTINGS CARD */}
        <div style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', padding: '1.75rem', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.25rem', borderBottom: '1px solid #EEF2E6', paddingBottom: '1rem' }}>
            <div style={{ background: '#DCEFB8', color: '#084734', padding: '8px', borderRadius: '10px' }}>
              <ShieldCheck size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#0D1F0F', margin: 0 }}>
                ⚽ Turf Booking Platform Fees
              </h3>
              <div style={{ fontSize: '0.8rem', color: '#6B7280' }}>
                Applied during regular turf slot bookings
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
            
            {/* Customer Fee */}
            <div style={{ background: '#F8FAF7', padding: '1.25rem', borderRadius: '14px', border: '1px solid #EEF2E6' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: '800', color: '#111827', display: 'block', marginBottom: '8px' }}>
                👤 Customer Platform Fee
              </label>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <select
                  value={settings.turf_customer_fee_type}
                  onChange={e => setSettings({ ...settings, turf_customer_fee_type: e.target.value })}
                  style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '0.88rem', fontWeight: '700' }}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="flat">Flat Amount (₹)</option>
                </select>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={settings.turf_customer_fee_value}
                  onChange={e => setSettings({ ...settings, turf_customer_fee_value: e.target.value })}
                  style={{ width: '110px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '0.88rem', fontWeight: '700' }}
                  placeholder="0"
                />
              </div>
              <div style={{ fontSize: '0.78rem', color: '#6B7280' }}>
                {settings.turf_customer_fee_type === 'percentage' 
                  ? `Customer will be charged ${settings.turf_customer_fee_value}% extra on slot total`
                  : `Customer will be charged ₹${settings.turf_customer_fee_value} flat fee on slot total`}
              </div>
            </div>

            {/* Vendor/Partner Fee */}
            <div style={{ background: '#F8FAF7', padding: '1.25rem', borderRadius: '14px', border: '1px solid #EEF2E6' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: '800', color: '#111827', display: 'block', marginBottom: '8px' }}>
                🏢 Partner / Vendor Platform Commission
              </label>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <select
                  value={settings.turf_partner_fee_type}
                  onChange={e => setSettings({ ...settings, turf_partner_fee_type: e.target.value })}
                  style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '0.88rem', fontWeight: '700' }}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="flat">Flat Amount (₹)</option>
                </select>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={settings.turf_partner_fee_value}
                  onChange={e => setSettings({ ...settings, turf_partner_fee_value: e.target.value })}
                  style={{ width: '110px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '0.88rem', fontWeight: '700' }}
                  placeholder="5"
                />
              </div>
              <div style={{ fontSize: '0.78rem', color: '#6B7280' }}>
                {settings.turf_partner_fee_type === 'percentage'
                  ? `Deduct ${settings.turf_partner_fee_value}% commission from vendor earnings`
                  : `Deduct ₹${settings.turf_partner_fee_value} flat commission from vendor earnings`}
              </div>
            </div>

          </div>
        </div>

        {/* TOURNAMENT SETTINGS CARD */}
        <div style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', padding: '1.75rem', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.25rem', borderBottom: '1px solid #EEF2E6', paddingBottom: '1rem' }}>
            <div style={{ background: '#FEF3C7', color: '#D97706', padding: '8px', borderRadius: '10px' }}>
              <IndianRupee size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#0D1F0F', margin: 0 }}>
                🏆 Tournament Registration Platform Fees
              </h3>
              <div style={{ fontSize: '0.8rem', color: '#6B7280' }}>
                Applied when players/teams register for tournaments
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
            
            {/* Customer Tournament Fee */}
            <div style={{ background: '#FFFBEB', padding: '1.25rem', borderRadius: '14px', border: '1px solid #FCD34D' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: '800', color: '#92400E', display: 'block', marginBottom: '8px' }}>
                👤 Customer Tournament Service Fee
              </label>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <select
                  value={settings.tournament_customer_fee_type}
                  onChange={e => setSettings({ ...settings, tournament_customer_fee_type: e.target.value })}
                  style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid #FCD34D', fontSize: '0.88rem', fontWeight: '700', background: 'white' }}
                >
                  <option value="flat">Flat Amount (₹)</option>
                  <option value="percentage">Percentage (%)</option>
                </select>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={settings.tournament_customer_fee_value}
                  onChange={e => setSettings({ ...settings, tournament_customer_fee_value: e.target.value })}
                  style={{ width: '110px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #FCD34D', fontSize: '0.88rem', fontWeight: '700', background: 'white' }}
                  placeholder="50"
                />
              </div>
              <div style={{ fontSize: '0.78rem', color: '#B45309' }}>
                {settings.tournament_customer_fee_type === 'percentage'
                  ? `Player will pay ${settings.tournament_customer_fee_value}% extra on tournament entry fee`
                  : `Player will pay ₹${settings.tournament_customer_fee_value} service fee on entry fee`}
              </div>
            </div>

            {/* Vendor/Partner Tournament Fee */}
            <div style={{ background: '#FFFBEB', padding: '1.25rem', borderRadius: '14px', border: '1px solid #FCD34D' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: '800', color: '#92400E', display: 'block', marginBottom: '8px' }}>
                🏢 Partner Tournament Organizer Commission
              </label>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <select
                  value={settings.tournament_partner_fee_type}
                  onChange={e => setSettings({ ...settings, tournament_partner_fee_type: e.target.value })}
                  style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid #FCD34D', fontSize: '0.88rem', fontWeight: '700', background: 'white' }}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="flat">Flat Amount (₹)</option>
                </select>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={settings.tournament_partner_fee_value}
                  onChange={e => setSettings({ ...settings, tournament_partner_fee_value: e.target.value })}
                  style={{ width: '110px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #FCD34D', fontSize: '0.88rem', fontWeight: '700', background: 'white' }}
                  placeholder="5"
                />
              </div>
              <div style={{ fontSize: '0.78rem', color: '#B45309' }}>
                {settings.tournament_partner_fee_type === 'percentage'
                  ? `Deduct ${settings.tournament_partner_fee_value}% commission from organizer tournament earnings`
                  : `Deduct ₹${settings.tournament_partner_fee_value} flat commission from organizer earnings`}
              </div>
            </div>

          </div>
        </div>

        {/* SUBMIT BUTTON */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              background: '#084734', color: '#CEF17B', border: 'none',
              padding: '14px 32px', borderRadius: '12px', fontWeight: '800',
              fontSize: '1rem', cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '8px',
              boxShadow: '0 8px 20px rgba(8,71,52,0.2)'
            }}
          >
            <Save size={18} /> {saving ? 'Saving Settings...' : 'Save Platform Fee Settings'}
          </button>
        </div>

      </form>
    </div>
  );
}
